import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { addWeeks } from 'date-fns'
import { runInAction } from 'mobx'
import qs from 'qs'
import { matchPath } from 'react-router'

import designTemplateService from './contentComponents/common/WorksheetDesign/DesignTemplate.service'
import { academyController } from './controller/academy.controller'
import { teacherController } from './controller/teacher.controller'
import modalService from './service/modal.service'
import { UserPermission } from './models/(Permission)/UserPermission'
import commonRepo from './repository/common.repo'
import {
  AUTH_ROUTES,
  type AuthRoute,
  getEntrancePage,
  NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH,
  PUBLIC_ROUTES,
  RouteName,
} from './router'
import { authService } from './service/auth.service'
import { curriculumCoreService } from './service/curriculumCore.service'
import { localStorageService } from './service/storage.service'
import type { AppDeviceInfo } from './service/webview.service'
import WebviewService from './service/webview.service'
import { remoteStorageService } from './utils/axios/remoteStorage'
import { initAxiosDefaultBaseURL } from './utils/axios/util'
import dateUtils from './utils/DateUtils'
import { envUtils } from './utils/envUtils'

type CRMLoginQSType = {
  accessToken?: string
  refreshToken?: string
  userId?: string
  academyId?: string
}

export type AfterLoginRedirectPathType = {
  redirectPathAfterLogin?: string
}

type SearchType = Partial<AppDeviceInfo> & CRMLoginQSType & AfterLoginRedirectPathType

export const checkAcademyInfoUpdatePeriod = (
  targetDate: Date,
  eventStartDate: Date,
  eventEndDate: Date = addWeeks(eventStartDate, 4),
) => {
  // TODO: 학원정보 업데이트 이벤트 기간이 아직 정해지지 않아서 시작일을 우선 임의 값으로 설정
  // - 차후 기간 업데이트 필요
  // - 로컬스토리지 값으로 테스트 가능하도록 구현

  const isEventPeriod = dateUtils.checkIsWithinInterval(new Date(), {
    startDate: eventStartDate,
    endDate: eventEndDate,
  })

  return {
    isEventPeriod,
    isUpdatedWithInEventPeriod:
      isEventPeriod &&
      dateUtils.checkIsWithinInterval(targetDate, {
        startDate: eventStartDate,
        endDate: eventEndDate,
      }),
  }
}

export const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some((publicRoute) =>
    matchPath(pathname, {
      path: publicRoute,
    }),
  )
}

const isNotAllowedToRedirectToLoginPath = (pathname: string) => {
  return NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH.some((route) =>
    matchPath(pathname, {
      path: route,
    }),
  )
}

/**
 * 로그인 페이지로 리다이렉트
 * 현재 경로를 redirectPathAfterLogin으로 설정
 */
const redirectToLoginPath = () => {
  const { pathname } = window.location

  // 현재 경로가 이미 로그인 페이지인지 체크
  const isLoginPathname =
    AUTH_ROUTES.includes(pathname as AuthRoute) || pathname === RouteName.serverMaintenance

  // 이미 로그인 페이지면 리다이렉트하지 않음
  if (isLoginPathname) {
    return
  }

  const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'

  const searchParams = new URLSearchParams(window.location.search)
  if (!isNotAllowedToRedirectToLoginPath(pathname)) {
    searchParams.set('redirectPathAfterLogin', pathname)
  }

  commonRepo.router.replace(`${loginPath}?${searchParams.toString()}`)
}

const authDaemon = async () => {
  const { search, pathname } = window.location

  const { accessToken, refreshToken, userId, academyId, redirectPathAfterLogin, ...webviewParams } =
    qs.parse(search, {
      ignoreQueryPrefix: true,
    }) as unknown as SearchType

  // CRM에서 로그인한 케이스
  if (accessToken && refreshToken && userId && academyId) {
    jwtTokenService.clearTokens()
    jwtTokenService.setTokens({ accessToken, refreshToken })
    authService.saveLoginResponseToStorage({
      userId,
      academyId,
    })

    const url = new URL(window.location.href)
    url.searchParams.delete('accessToken')
    url.searchParams.delete('refreshToken')
    url.searchParams.delete('userId')
    url.searchParams.delete('academyId')
    history.replaceState(null, '', url)
  }

  // JWT 인증 초기화
  try {
    await authService.initializeJwtAuth()
  } catch (error) {
    // 인증 초기화 실패 시 스토리지 클리어 후 로그인 페이지로 리다이렉트
    jwtTokenService.clearTokens()
    sessionStorage.clear()
    localStorage.clear()
    redirectToLoginPath()
    return
  }

  // TODO: axios.default.baseURL 세팅이 화면단에서 이뤄져서 authDaemon보다 늦어져서
  // authDaemon 내 api가 세팅되었어야할 baseURL과 달라짐.
  // 일단 화면단의 로직을 공통함수로 빼서 authDaemon 상단에서 먼저 실행하는 식으로 고쳤지만,
  // 더 좋은 방법이 있으면 수정할 것.
  if (envUtils.isDev) {
    initAxiosDefaultBaseURL(localStorageService.getItem('DEV_API') ?? 'dev')
  }

  // 웹뷰 설정
  if (
    WebviewService.isAppAndroid &&
    webviewParams &&
    webviewParams.device &&
    webviewParams.version
  ) {
    WebviewService.device = webviewParams.device
    WebviewService.version = parseInt(webviewParams.version)
  }

  if (isPublicRoute(pathname)) return

  // 임시 - 차후 localstorageService로 교체
  const teacherId = localStorage.getItem('teacher-id')
  if (teacherId) {
    localStorageService.setItem('TEACHER_ID')(teacherId)
  }

  // 로그인 되어 있다면
  if (jwtTokenService.isLoggedIn) {
    // [임시] API 미구현 상태 — 로그인 후 빈 페이지로 바로 이동
    const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
    if (pathname === loginPath || pathname === '/') {
      commonRepo.router.push(redirectPathAfterLogin || getEntrancePage())
    }

    // TODO: ScienceFlat
    // remoteStorageService.initRelationId(teacherId)
    // remoteStorageService.get('visitedWorksheetBoardView')
    // remoteStorageService.get('visitedWorkbookBoardView')
    // remoteStorageService.get('ONE_CLICK_WORKSHEET_OPTIONS')
    // remoteStorageService.get('ESSAY_WORKSHEET_OPEN_NOTIFICATION')
    // remoteStorageService.get('PUBLIC_WORKBOOK_OPEN_NOTIFICATION')
    try {
      // TODO: ScienceFlat
      designTemplateService.fetchAllDesignTemplateList()
      const [user, academy] = await Promise.all([
        teacherController.fetchUserTeacher(),
        academyController.fetchMyAcademy(),
      ])

      localStorageService.setItem('ACADEMY_ID')(user.academyId)

      runInAction(() => {
        commonRepo.user = user
        commonRepo.academy = academy
        commonRepo.permission = new UserPermission({
          userAuthority: user.type,
          academyFeatureAuthority: academy.authority,
          subscribedCurriculums: academy.subscribeItems,
          payStatus: academy.payStatus,
          isLimited: commonRepo.permission.isLimited,
          isSeoltab: false,
          isFranchise: academy.isFranchise,
        })
      })

      try {
        await curriculumCoreService.fetchSetCurriculum({
          plan: commonRepo.isSolo ? 'solo' : 'default',
          subscribedCurriculums: commonRepo.permission.byFeature.솔로_구독_커리큘럼,
        })
      } catch (error) {
        console.error(error)
        return
      }

      const isFirstLogin = await teacherController.markLogin().catch(() => false)

      const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
      if (pathname === loginPath || pathname === '/') {
        commonRepo.router.push(redirectPathAfterLogin || getEntrancePage())
      }

      if (isFirstLogin) {
        modalService.openFirstLoginWelcomeModal()
      }
    } catch (e) {
      console.error(e)
      jwtTokenService.clearTokens()
      if (accessToken || refreshToken || academyId || userId) {
        const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
        window.location.replace(loginPath)
        return
      }
      redirectToLoginPath()
    }
  } else {
    //token이 없다면
    //다른 탭이 유효한 토큰을 갖고 있는지 req날림 - 갖고있다면 토큰값 갖고 페이지 리로드 되면서 여기서 끊김
    jwtTokenService.requestToken()
    // 다른 탭 토큰 동기화를 잠시 대기한 뒤, 최종 실패 시 로그인으로 리다이렉트
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      if (jwtTokenService.isLoggedIn) {
        window.clearInterval(timer)
        return
      }
      if (Date.now() - startedAt >= 3000) {
        window.clearInterval(timer)
        redirectToLoginPath()
      }
    }, 100)
  }
}

export default authDaemon
